import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Paper,
  Stack,
  Avatar,
  IconButton,
  Divider,
  Button,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Casino as CasinoIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import StatsCards from '../components/StatsCards';
import RaceChip from '../components/RaceChip';
import OptimizedImage from '../components/OptimizedImage';
import HuevoDragon from '../assets/medals2/Huevo_de_Dragon_v2.png';

// Definir keyframes para la animación de gradiente
const gradientGlowAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

function UserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, handleLogout } = useContext(AuthContext);
  const [targetUser, setTargetUser] = useState(null);
  const [matches, setMatches] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user details
        const userResponse = await api.get(`/api/users/${id}`);
        setTargetUser(userResponse.data);

        // Fetch user's matches
        const matchesResponse = await api.get(`/api/matches/me/${id}`);
        setMatches(matchesResponse.data);

        // Fetch user's decks
        const decksResponse = await api.get(`/api/decks/user/${id}`);
        setDecks(decksResponse.data);

        setError(null);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Error al cargar los datos del usuario');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Agregar efecto para reiniciar el scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 120px)' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !targetUser) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: 2,
        p: 3 
      }}>
        <Typography color="error">{error || 'Usuario no encontrado'}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Volver
        </Button>
      </Box>
    );
  }

  const defaultDeck = decks.find(deck => deck.is_default);

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        py: 3,
        px: { xs: 0.5, sm: 1 },
        width: '88%',
        maxWidth: '500px !important'
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ 
            p: 1.8,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton 
                  onClick={() => navigate(-1)}
                  sx={{ 
                    width: 40,
                    height: 40,
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '&:hover': { 
                      bgcolor: 'primary.main',
                      color: 'white',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Perfil de Usuario
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem'
                  }}
                >
                  {targetUser.username[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {targetUser.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID #{targetUser.id}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="img"
                src={HuevoDragon}
                alt="Dragon Egg"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientGlowAnimation} 3s ease infinite`,
                    zIndex: -1,
                    filter: 'blur(8px)',
                    opacity: 0.7
                  }
                }}
              />
            </Box>
          </Stack>
        </Paper>

        {/* Stats Cards */}
        <Box>
          <StatsCards matches={matches} user={targetUser} />
        </Box>

        {/* Default Deck */}
        {defaultDeck && (
          <Paper
            elevation={0}
            sx={{ 
              p: 1.8,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CasinoIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Mazo Default
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                mt: 1
              }}>
                <Typography variant="subtitle1">
                  {defaultDeck.name}
                </Typography>
                <RaceChip race={defaultDeck.raza} />
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default UserPage; 